import type { ReactNode } from 'react'

import { Minus, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface StepperProps {
    id: string
    value: number
    min: number
    max: number
    onChange: (value: number) => void
    invalid?: boolean
    describedBy?: string
    singular?: string
    plural?: string
}

const Stepper = ({
    id,
    value,
    min,
    max,
    onChange,
    invalid = false,
    describedBy,
    singular = 'guest',
    plural = 'guests'
}: StepperProps): ReactNode => {
    const [draft, setDraft] = useState(String(value))

    useEffect(() => {
        setDraft(String(value))
    }, [value])

    const commit = (raw: string) => {
        const parsed = parseInt(raw, 10)

        if (Number.isNaN(parsed)) {
            setDraft(String(value))
            return
        }

        const next = Math.min(max, Math.max(min, parsed))

        setDraft(String(next))
        onChange(next)
    }

    const buttonClass = cn(
        'flex size-12 shrink-0 items-center justify-center rounded-full border border-ivory/20 text-ivory/80',
        'transition-colors duration-500 ease-film hover:border-brass hover:text-brass-bright',
        'disabled:pointer-events-none disabled:opacity-30'
    )

    return (
        <div
            className={cn(
                'flex items-center justify-between gap-4 border-b py-2 transition-colors duration-500',
                invalid ? 'border-destructive/80' : 'border-ivory/20'
            )}
        >
            <button
                type='button'
                className={buttonClass}
                aria-label={`Fewer ${plural}`}
                aria-controls={id}
                disabled={value <= min}
                onClick={() => onChange(Math.max(min, value - 1))}
            >
                <Minus aria-hidden='true' className='size-4' strokeWidth={1.25} />
            </button>

            <div className='flex items-baseline gap-3'>
                <input
                    id={id}
                    type='text'
                    inputMode='numeric'
                    autoComplete='off'
                    value={draft}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    onChange={event => setDraft(event.target.value.replace(/\D/g, '').slice(0, 3))}
                    onBlur={event => commit(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'ArrowUp') {
                            event.preventDefault()
                            onChange(Math.min(max, value + 1))
                        }

                        if (event.key === 'ArrowDown') {
                            event.preventDefault()
                            onChange(Math.max(min, value - 1))
                        }

                        if (event.key === 'Enter') {
                            event.preventDefault()
                            commit(event.currentTarget.value)
                        }
                    }}
                    className='w-[3ch] bg-transparent text-center font-heading text-[2.4rem] leading-none tabular-nums text-ivory focus:outline-none'
                />

                <span aria-hidden='true' className='text-sm text-ivory/55'>
                    {value === 1 ? singular : plural}
                </span>
            </div>

            <button
                type='button'
                className={buttonClass}
                aria-label={`More ${plural}`}
                aria-controls={id}
                disabled={value >= max}
                onClick={() => onChange(Math.min(max, value + 1))}
            >
                <Plus aria-hidden='true' className='size-4' strokeWidth={1.25} />
            </button>

            <span className='sr-only' aria-live='polite'>
                {value} {value === 1 ? singular : plural}
            </span>
        </div>
    )
}

export default Stepper
